import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Table } from "react-bootstrap";
import { ContentCatalogSettingsService } from "@services/contentCatalogSettings/ContentCatalogSettingsService";
import { appStore } from "@store/appStore";
import RtfEditor from "../../../components/common/editor/rtfEditor";
import MotiModal from "../../../shared/components/MotiModal";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// --- Types ---

type SectionItem = {
    id: number;
    name: string;
    hero: string | null;
    copy_text: string;
    enabled: boolean;
    tags?: SectionItem[];
};

type Section = {
    type: "collections" | "tag_groups";
    items: SectionItem[];
};

type StoreSettings = {
    hero: string;
    copy_text: string;
    sections: Section[];
};

type ItemPath = {
    sectionType: "collections" | "tag_groups";
    sectionIndex: number;
    itemIndex: number;
    tagIndex?: number;
};

type Props = {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
};

// --- Helpers ---

function resolveItem(settings: StoreSettings, path: ItemPath): SectionItem | null {
    const section = settings.sections[path.sectionIndex];
    if (!section) return null;
    const item = section.items[path.itemIndex];
    if (!item) return null;
    if (path.tagIndex !== undefined) return item.tags?.[path.tagIndex] ?? null;
    return item;
}

function updateItemAtPath(
    settings: StoreSettings,
    path: ItemPath,
    updates: Partial<Pick<SectionItem, "hero" | "copy_text" | "enabled">>,
): StoreSettings {
    const clone: StoreSettings = structuredClone(settings);
    const item = resolveItem(clone, path);
    if (!item) return clone;
    Object.assign(item, updates);
    return clone;
}

// --- Component ---

export default function StoreSettingsModal({ isOpen, setIsOpen }: Props) {
    const service = useMemo(() => new ContentCatalogSettingsService(), []);
    const organizationId = appStore.loggedInUser.organization.id;

    const [settings, setSettings] = useState<StoreSettings | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Store-level form state
    const [storeHero, setStoreHero] = useState<File | null>(null);
    const [storeHeroPreview, setStoreHeroPreview] = useState("");
    const [storeCopy, setStoreCopy] = useState("");

    // Item editing
    const [editingPath, setEditingPath] = useState<ItemPath | null>(null);
    const [itemHero, setItemHero] = useState<File | null>(null);
    const [itemHeroPreview, setItemHeroPreview] = useState("");
    const [itemCopy, setItemCopy] = useState("");
    const [itemEnabled, setItemEnabled] = useState(false);

    const editingItem = editingPath && settings ? resolveItem(settings, editingPath) : null;

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await service.getStoreSettings(organizationId);
            if (!response) return;
            setSettings(response);
            setStoreHeroPreview(response.hero || "");
            setStoreCopy(response.copy_text || "");
        } finally {
            setLoading(false);
        }
    }, [service, organizationId]);

    useEffect(() => {
        if (!isOpen) return;
        loadSettings();
        setEditingPath(null);
        setStoreHero(null);
        setItemHero(null);
    }, [isOpen, loadSettings]);

    // --- Store save ---

    const handleSaveStore = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const payload = { ...settings, copy_text: storeCopy };
            await service.saveStoreSettings(payload, organizationId, "Home");
            if (storeHero) {
                await service.uploadStoreHero(storeHero, organizationId);
            }
            setStoreHero(null);
            await loadSettings();
        } finally {
            setSaving(false);
        }
    };

    // --- Item editing ---

    const openItem = (path: ItemPath) => {
        if (!settings) return;
        const item = resolveItem(settings, path);
        if (!item) return;
        setEditingPath(path);
        setItemHeroPreview(item.hero || "");
        setItemCopy(item.copy_text || "");
        setItemEnabled(item.enabled);
        setItemHero(null);
    };

    const handleSaveItem = async () => {
        if (!settings || !editingPath) return;
        setSaving(true);
        try {
            const updated = updateItemAtPath(settings, editingPath, {
                copy_text: itemCopy,
                enabled: itemEnabled,
            });
            await service.saveStoreSettings(updated, organizationId, "Home");
            if (itemHero) {
                const item = resolveItem(settings, editingPath)!;
                if (editingPath.sectionType === "collections") {
                    await service.uploadCollectionHero(itemHero, organizationId, item.id);
                } else {
                    await service.uploadTagHero(itemHero, organizationId, item.id);
                }
            }
            setEditingPath(null);
            setItemHero(null);
            await loadSettings();
        } finally {
            setSaving(false);
        }
    };

    const handleCloseItem = () => {
        if (saving) return;
        setEditingPath(null);
    };

    // --- Tag group enabled toggle ---

    const handleToggleTagGroup = async (sectionIndex: number, itemIndex: number) => {
        if (!settings) return;
        const updated = updateItemAtPath(
            settings,
            { sectionType: "tag_groups", sectionIndex, itemIndex },
            { enabled: !settings.sections[sectionIndex].items[itemIndex].enabled },
        );
        setSettings(updated);
        await service.saveStoreSettings(updated, organizationId, "Home");
    };

    // --- Render ---

    return (
        <MotiModal isOpen={isOpen} setIsOpen={setIsOpen} hideX={!!editingPath}>
            <MotiModal.Header>
                {editingPath && (
                    <IconButton edge="start" color="inherit" onClick={handleCloseItem} style={{ color: "#845adf" }}>
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Typography sx={{ flex: 1 }} variant="h6" component="div" style={{ color: "var(--default-text-color)" }}>
                    {editingPath ? editingItem?.name ?? "Edit Item" : "Store Settings"}
                </Typography>
            </MotiModal.Header>

            <MotiModal.Body>
                <div className="p-4" style={{ margin: "0 auto", overflowY: "auto", height: "100%" }}>
                    {loading && <div className="text-center py-5"><span className="spinner-border" /></div>}

                    {!loading && settings && !editingPath && (
                        <MainSettingsView
                            heroPreview={storeHeroPreview}
                            copyText={storeCopy}
                            sections={settings.sections}
                            saving={saving}
                            onHeroChange={(file) => {
                                setStoreHero(file);
                                setStoreHeroPreview(URL.createObjectURL(file));
                            }}
                            onCopyChange={setStoreCopy}
                            onSaveStore={handleSaveStore}
                            onEditItem={openItem}
                            onToggleTagGroup={handleToggleTagGroup}
                        />
                    )}

                    {!loading && editingPath && editingItem && (
                        <ItemSettingsView
                            heroPreview={itemHeroPreview}
                            copyText={itemCopy}
                            enabled={itemEnabled}
                            saving={saving}
                            onHeroChange={(file) => {
                                setItemHero(file);
                                setItemHeroPreview(URL.createObjectURL(file));
                            }}
                            onCopyChange={setItemCopy}
                            onEnabledChange={setItemEnabled}
                            onSave={handleSaveItem}
                        />
                    )}
                </div>
            </MotiModal.Body>
        </MotiModal>
    );
}

// --- Sub-views ---

function MainSettingsView({
    heroPreview,
    copyText,
    sections,
    saving,
    onHeroChange,
    onCopyChange,
    onSaveStore,
    onEditItem,
    onToggleTagGroup,
}: {
    heroPreview: string;
    copyText: string;
    sections: Section[];
    saving: boolean;
    onHeroChange: (file: File) => void;
    onCopyChange: (text: string) => void;
    onSaveStore: () => void;
    onEditItem: (path: ItemPath) => void;
    onToggleTagGroup: (sectionIndex: number, itemIndex: number) => void;
}) {
    return (
        <>
            <Card className="custom-card mb-3">
                <Card.Header>
                    <Card.Title>Store</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Form.Label>Hero</Form.Label>
                    {heroPreview && (
                        <div className="mb-2">
                            <img src={heroPreview} alt="Store hero" className="store-hero-preview" />
                        </div>
                    )}
                    <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) onHeroChange(file);
                        }}
                    />

                    <Form.Label className="mt-3">Copy</Form.Label>
                    <RtfEditor value={copyText} onChange={onCopyChange} />

                    <div className="d-flex justify-content-end mt-3">
                        <Button onClick={onSaveStore} disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {sections.map((section, sectionIndex) => (
                <Card key={section.type} className="custom-card mb-3">
                    <Card.Header>
                        <Card.Title className="text-capitalize">{section.type.replace("_", " ")}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {section.type === "collections" && (
                            <ItemTable
                                items={section.items}
                                onEdit={(itemIndex) =>
                                    onEditItem({ sectionType: "collections", sectionIndex, itemIndex })
                                }
                            />
                        )}

                        {section.type === "tag_groups" && section.items.map((tagGroup, tagGroupIndex) => (
                            <Card key={tagGroup.id} className="custom-card mb-2">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <Card.Title className="mb-0">{tagGroup.name}</Card.Title>
                                    <Form.Check
                                        label="Enabled"
                                        checked={tagGroup.enabled}
                                        onChange={() => onToggleTagGroup(sectionIndex, tagGroupIndex)}
                                    />
                                </Card.Header>
                                <Card.Body>
                                    <ItemTable
                                        items={tagGroup.tags ?? []}
                                        onEdit={(tagIndex) =>
                                            onEditItem({
                                                sectionType: "tag_groups",
                                                sectionIndex,
                                                itemIndex: tagGroupIndex,
                                                tagIndex,
                                            })
                                        }
                                    />
                                </Card.Body>
                            </Card>
                        ))}
                    </Card.Body>
                </Card>
            ))}
        </>
    );
}

function ItemTable({ items, onEdit }: { items: SectionItem[]; onEdit: (index: number) => void }) {
    if (items.length === 0) return null;

    return (
        <Table className="table table-hover table-bordered mb-0" style={{ tableLayout: "fixed" }}>
            <thead>
                <tr>
                    <th className="bg-primary text-white">Name</th>
                </tr>
            </thead>
            <tbody>
                {items.map((item, index) => (
                    <tr key={item.id}>
                        <td>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-bold fs-14">{item.name}</span>
                                <Button size="sm" variant="outline-primary" onClick={() => onEdit(index)}>
                                    <i className="bi bi-pencil" />
                                </Button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

function ItemSettingsView({
    heroPreview,
    copyText,
    enabled,
    saving,
    onHeroChange,
    onCopyChange,
    onEnabledChange,
    onSave,
}: {
    heroPreview: string;
    copyText: string;
    enabled: boolean;
    saving: boolean;
    onHeroChange: (file: File) => void;
    onCopyChange: (text: string) => void;
    onEnabledChange: (enabled: boolean) => void;
    onSave: () => void;
}) {
    return (
        <Card className="custom-card">
            <Card.Header>
                <Card.Title>Item Settings</Card.Title>
            </Card.Header>
            <Card.Body>
                <Form.Label>Hero</Form.Label>
                {heroPreview && (
                    <div className="mb-2">
                        <img src={heroPreview} alt="Item hero" className="store-hero-preview" />
                    </div>
                )}
                <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) onHeroChange(file);
                    }}
                />

                <Form.Label className="mt-3">Copy</Form.Label>
                <RtfEditor value={copyText} onChange={onCopyChange} />

                <Form.Check
                    label="Enabled"
                    checked={enabled}
                    className="mt-3"
                    onChange={() => onEnabledChange(!enabled)}
                />

                <div className="d-flex justify-content-end mt-3">
                    <Button onClick={onSave} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
}
